// Import (Phase 7): Kennzeichen aus einer eingefuegten Liste oder einer
// CSV-Datei in die aktive Sammlung eintragen. Flaeche, die es im Original
// nicht gibt, deshalb frei gestaltet.
//
// Nur CSV/Klartext. Die native Backup-Datei der Original-App laesst sich
// nicht sicher zuordnen (siehe api/src/routes/collections.ts) - das
// bleibt offen, bis es entweder die interne Sortierung der Original-App
// gibt oder jemand ein paar Eintraege von Hand zum Abgleich bestaetigt.
//
// Ohne Vorsatz gilt Deutschland, "PL:ZS" holt das polnische. Geraten wird
// nichts: derselbe Code gibt es in mehreren Laendern (A ist Augsburg und
// Prag), deshalb muss das Land dranstehen, wenn es nicht Deutschland ist.

import { useRef, useState } from "react";
import { aktiveSammlung } from "../lib/collections.js";
import { fehlertext } from "../lib/api.js";
import { parseCodesEingabe, useImportieren, type ImportErgebnis } from "../lib/import.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import { Karte, Knopf, Hinweis } from "../bausteine/Bausteine.js";
import { Unterseite } from "../bausteine/Unterseite.js";

export function Import({ nutzer, sammlungen }: { nutzer: Nutzer; sammlungen: Sammlung[] }) {
  const [text, setText] = useState("");
  const [ergebnis, setErgebnis] = useState<ImportErgebnis | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const dateiEingabe = useRef<HTMLInputElement>(null);

  const activeCollectionId = (nutzer.settings as { activeCollectionId?: number } | undefined)?.activeCollectionId;
  const sammlung = aktiveSammlung(sammlungen, activeCollectionId);
  const importieren = useImportieren(sammlung?.id);

  async function dateiGelesen(datei: File) {
    setText(await datei.text());
  }

  async function absenden() {
    setFehler(null);
    setErgebnis(null);
    const codes = parseCodesEingabe(text);
    if (codes.length === 0) return;
    try {
      const antwort = await importieren.mutateAsync(codes);
      setErgebnis(antwort);
    } catch (e) {
      setFehler(fehlertext(e));
    }
  }

  return (
    <Unterseite
      titel="Kennzeichen importieren"
      kinder={
        <>
          <Karte
            kinder={
              <>
                <p className="mb-1 text-sm text-neutral-600">
                  Fügt Codes ein oder ladet eine CSV-Datei, getrennt durch Komma, Zeilenumbruch oder
                  Leerzeichen. Landet in: <strong>{sammlung?.name ?? "–"}</strong>.
                </p>
                <p className="mb-3 text-sm text-neutral-500">
                  Ohne Angabe gilt Deutschland. Für andere Länder das Kürzel davorsetzen:{" "}
                  <code className="rounded bg-neutral-100 px-1">PL:ZS</code>,{" "}
                  <code className="rounded bg-neutral-100 px-1">CZ:A</code>.
                </p>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={"A, KA, M\nHH, PL:ZS, CZ:A"}
                  rows={6}
                  className="mb-3 w-full rounded border border-neutral-300 px-3 py-2 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <Knopf onClick={() => void absenden()} disabled={importieren.isPending} kinder="Importieren" />
                  <Knopf art="neben" onClick={() => dateiEingabe.current?.click()} kinder="CSV-Datei wählen" />
                  <input
                    ref={dateiEingabe}
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain"
                    className="hidden"
                    onChange={(e) => {
                      const datei = e.target.files?.[0];
                      if (datei) void dateiGelesen(datei);
                    }}
                  />
                </div>
              </>
            }
          />

          {fehler && <Hinweis art="fehler" kinder={fehler} />}

          {ergebnis && (
            <Karte
              kinder={
                <>
                  <h2 className="mb-2 text-base font-medium">Ergebnis</h2>
                  <p className="text-sm text-neutral-700">{ergebnis.importiert} neu eingetragen</p>
                  <p className="text-sm text-neutral-700">{ergebnis.bereitsVorhanden} waren schon in der Sammlung</p>
                  {ergebnis.unbekannt.length > 0 && (
                    <>
                      <p className="mt-2 text-sm text-neutral-700">
                        {ergebnis.unbekannt.length} nicht erkannt:
                      </p>
                      <p className="text-sm text-neutral-500">{ergebnis.unbekannt.join(", ")}</p>
                    </>
                  )}
                </>
              }
            />
          )}
        </>
      }
    />
  );
}
